import { Link } from "@inertiajs/react"
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card } from "@/components/ui/Card"
import { formatMoney, formatMoneyFull } from "@/lib/money"

export interface BarSeries {
  key: string
  name: string
  color: string
}

interface Props {
  title: string
  subtitle: string
  currency: string
  data: Record<string, string | number | null>[]
  /** Stacked in order, bottom first. */
  bars: BarSeries[]
  /** Optional plan line drawn over the bars. */
  line?: { key: string; name: string }
  /** Optional link to the full page, shown under the chart. */
  footer?: { label: string; href: string }
}

// Same axis, grid and tooltip styling as the Revenue page charts.
export default function MonthlyRevenueChart({ title, subtitle, currency, data, bars, line, footer }: Props) {
  return (
    <Card className="px-5 pb-3 pt-4">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        {footer && (
          <Link href={footer.href} className="shrink-0 text-sm font-medium text-link underline-offset-4 hover:underline">
            {footer.label} →
          </Link>
        )}
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="25%">
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => formatMoney(Number(v), currency)} tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} width={64} />
          <Tooltip
            formatter={(v) => formatMoneyFull(Number(v), currency)}
            cursor={{ fill: "var(--muted)" }}
            contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--border)" }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={10} itemSorter={null} />
          {bars.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              stackId="revenue"
              fill={s.color}
              stroke="var(--card)"
              strokeWidth={1}
              maxBarSize={28}
              radius={i === bars.length - 1 ? [4, 4, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
          {line && (
            <Line
              dataKey={line.key}
              name={line.name}
              type="monotone"
              stroke="var(--chart-axis)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  )
}
